import {DOCUMENT} from '@angular/common';
import {computed, Directive, ElementRef, Inject, input, effect, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {arrow, computePosition, flip, offset, shift} from '@floating-ui/dom';

const SHOW = "data-show";

@Directive({
  standalone: true,
  selector: '[tooltip]'
})
export class TooltipDirective implements OnInit, OnDestroy {
  /**
   * Defines the tooltip to be shown. Can be HTML.
    */
  tooltip = input<string>();

  private tooltipContentElement: HTMLElement;
  private arrowElement: HTMLElement;
  private eventListeners: Array<Function>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private element: ElementRef,
    private renderer: Renderer2
  ) {
    effect(() => {
      this.tooltipContentElement.innerHTML = this.tooltip();
    });
  }

  ngOnInit(): void {
    if (!this.tooltip() || this.tooltip() === "") {
      return;
    }
    this.element.nativeElement.classList.add("tooltip");
    this.tooltipContentElement = this.document.createElement("div");
    this.tooltipContentElement.classList.add("tooltip__content");
    this.tooltipContentElement.innerHTML = this.tooltip();

    this.arrowElement = this.document.createElement("div");
    this.arrowElement.classList.add("tooltip__content-arrow");
    this.renderer.appendChild(this.tooltipContentElement, this.arrowElement);

    this.renderer.appendChild(
      this.element.nativeElement,
      this.tooltipContentElement
    );

    this.eventListeners = [
      this.renderer.listen(this.element.nativeElement, "mouseenter", () =>
        this.show()
      ),
      this.renderer.listen(this.element.nativeElement, "mouseleave", () =>
        this.hide()
      )
    ];
  }

  update(): void {
    if (!this.tooltip() || this.tooltip() === "") {
      return;
    }

    computePosition(this.element.nativeElement, this.tooltipContentElement, {
      placement: "top",
      middleware: [
        offset(15),
        flip(),
        shift({padding: 5}),
        arrow({element: this.arrowElement})
      ]
    }).then(({x, y, placement, middlewareData}) => {
      Object.assign(this.tooltipContentElement.style, {
        left: `${x}px`,
        top: `${y}px`
      });

      const {x: arrowX, y: arrowY} = middlewareData.arrow;

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right"
      }[placement.split("-")[0]];

      Object.assign(this.arrowElement.style, {
        left: arrowX != null ? `${arrowX}px` : "",
        top: arrowY != null ? `${arrowY}px` : "",
        right: "",
        bottom: "",
        [staticSide]: "-4px"
      });
    });
  }

  ngOnDestroy() {
    this.tooltipContentElement.remove();
    if (this.eventListeners?.length) {
      this.eventListeners.forEach((removeEventListenerFn) =>
        removeEventListenerFn()
      );
    }
  }

  private show(): void {
    this.tooltipContentElement?.setAttribute(SHOW, "");
    this.tooltipContentElement?.setAttribute("aria-hidden", "false");
    this.update();
  }

  private hide(): void {
    this.tooltipContentElement?.removeAttribute(SHOW);
    this.tooltipContentElement?.setAttribute("aria-hidden", "true");
  }
}
